import { useEffect, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Heart } from 'lucide-react'
import { openExternalLink } from '../lib/telegram'
import { tosURL, privacyURL } from '../lib/legal'
import { reportOnboardingStep } from '../lib/api'

/**
 * First screen a brand-new user sees, before the onboarding wizard. Logo,
 * tagline, primary CTA, and an affirmative consent checkbox that gates the
 * CTA — a passive sentence alone ("by creating a profile you agree...") has
 * no evidentiary value if it's ever disputed; ticking a box the user had to
 * actively interact with does.
 *
 * The checkbox state itself is just UI, though — the durable evidence of
 * consent is server-side: tapping through to onboarding and creating a
 * profile is the affirmative act consent attaches to, and
 * handleCreateProfile (apps/api/handlers.go) stamps
 * users.tos_accepted_at / tos_version at that moment (see migration 000020
 * and UserRepo.SetTOSAcceptedIfUnset, which re-stamps on every ToS version
 * bump, not just the first time).
 */
export default function WelcomePage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [agreed, setAgreed] = useState(false)

  // Report reaching this screen for the admin /stats abandonment breakdown
  // (domain.OnboardingSteps' "welcome" entry) — this is the very first
  // screen mounted after a successful Mini App boot, so it's what splits
  // "opened fine but bounced off consent" apart from "the app never loaded
  // (or crashed) at all", which the "welcome" step didn't exist to
  // distinguish before. Same best-effort convention as
  // OnboardingPage.tsx/PhotoUploadPage.tsx's calls: never blocks the UX.
  useEffect(() => {
    reportOnboardingStep('welcome').catch(() => {})
  }, [])

  return (
    <div className="flex min-h-dvh flex-col items-center justify-between bg-tg-bg px-6 py-12">
      <div />

      <div className="flex flex-col items-center text-center">
        <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-primary">
          <Heart className="h-10 w-10 fill-white text-white" />
        </div>
        <h1 className="text-2xl font-semibold text-tg-text">{t('welcome.appName')}</h1>
        <p className="mt-2 max-w-xs text-sm text-tg-hint">{t('welcome.tagline')}</p>
      </div>

      <div className="w-full">
        <label className="mb-4 flex items-start gap-2 text-left text-xs text-tg-hint">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
          />
          <span>
            <Trans
              i18nKey="welcome.consent"
              components={{
                tos: (
                  <button
                    className="underline"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      openExternalLink(tosURL(i18n.language))
                    }}
                  />
                ),
                privacy: (
                  <button
                    className="underline"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      openExternalLink(privacyURL(i18n.language))
                    }}
                  />
                ),
              }}
            />
          </span>
        </label>
        <button
          onClick={() => navigate('/onboarding')}
          disabled={!agreed}
          className="w-full rounded-xl bg-primary py-4 font-medium text-white disabled:opacity-40"
        >
          {t('welcome.createProfile')}
        </button>
      </div>
    </div>
  )
}
